import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckboxInput, TextInput } from "@streeteasy/design-system/form";
import axios from "axios";
import Cookie from "js-cookie";
import { oneOf, string } from "prop-types";

import priceOptions from "src/shared/constants/priceOptions";

import HomePageLocationFilterWidget from "../../../home-page-location-filter-widget";
import { getInitialAreas } from "../../../home-page-location-filter-widget/components/sideEffects";
import { searchPriceType, updateSearchRequestParams } from "../../constants";
import { locations } from "../../propTypes";
import { setForRent, setForSale } from "../../redux/slices/listingTypeSlice";
import * as S from "./styled";

const SearchModule = ({ areaOptions, lastSearchType, priceWidgetTest }) => {
  const listingTypeSelector = state => state.listingType;
  const { listingType } = useSelector(listingTypeSelector);
  const [areaIDs, setAreaIDs] = useState(null);
  const [priceSelections, updatePriceSelections] = useState({
    [searchPriceType.MIN]: "",
    [searchPriceType.MAX]: ""
  });
  const [listingTypeTab, setListingTypeTab] = useState(
    lastSearchType || "rentals"
  );
  const [noFee, setNoFee] = useState(false);
  const toFormattedValue = num => {
    if (Number(num) === 0) return "";

    const formatter = new Intl.NumberFormat("en-US", {
      currency: "USD",
      minimumFractionDigits: 0,
      style: "currency"
    });

    return formatter.format(num);
  };
  const dispatch = useDispatch();

  const isNoFee = noFee && listingType === "for-rent";

  const applySelectedAreas = () => {
    setAreaIDs(
      getInitialAreas(areaOptions).length
        ? getInitialAreas(areaOptions).map(area => area.id)
        : null
    );
  };

  const fromFormattedValue = val => {
    const strippedValue = val.replace(/\D/g, "");

    return Number(strippedValue);
  };

  const handlePriceChange = (price, type) => {
    updatePriceSelections({
      ...priceSelections,
      [type]: toFormattedValue(fromFormattedValue(price))
    });
  };

  const handleSearchSubmit = async () => {
    const gaListingClass = listingType === "for-sale" ? "Sale" : "Rental";
    const gaAreaIDs = getInitialAreas(areaOptions).length
      ? getInitialAreas(areaOptions)
          .map(area => area.id)
          .join(",")
      : "1";
    const gaMaxPrice = priceSelections[searchPriceType.MAX]
      ? fromFormattedValue(priceSelections[searchPriceType.MAX])
      : "";
    const gaMinPrice = priceSelections[searchPriceType.MIN]
      ? fromFormattedValue(priceSelections[searchPriceType.MIN])
      : "";
    const gaPrice =
      priceSelections[searchPriceType.MIN] ||
      priceSelections[searchPriceType.MAX]
        ? `|price:${gaMinPrice}-${gaMaxPrice}`
        : "";
    const gaNoFee = isNoFee ? "|no_fee:1" : "";

    const csrfTokenElement = document.querySelector('[name="csrf-token"]');
    const csrfToken = csrfTokenElement ? csrfTokenElement.content : "";

    window.dataLayer.push({
      event: "custom_event",
      event_category: "search",
      event_action: "submit",
      event_label: `listing_class:${gaListingClass}|area:${gaAreaIDs}${gaPrice}${gaNoFee}`
    });

    await axios
      .post(
        `${window.location.origin}/nyc/process/${listingTypeTab}/update_search`,
        {
          ...updateSearchRequestParams,
          area: [areaIDs],
          no_fee: isNoFee ? "1" : null,
          price_from: priceSelections[searchPriceType.MIN],
          price_to: priceSelections[searchPriceType.MAX]
        },
        {
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "x-csrf-token": csrfToken
          }
        }
      )
      .then(response => {
        const { search_url } = response.data;

        window.location.href = search_url;
      })
      .catch(error => {
        console.log(error);
      });
  };

  const handleSetRent = () => {
    setListingTypeTab("rentals");
    dispatch(setForRent());
    Cookie.set("last_search_tab", "rentals");
    updatePriceSelections({
      [searchPriceType.MIN]: "",
      [searchPriceType.MAX]: ""
    });
  };

  const handleSetBuy = () => {
    setListingTypeTab("sales");
    dispatch(setForSale());
    Cookie.set("last_search_tab", "sales");
    updatePriceSelections({
      [searchPriceType.MIN]: "",
      [searchPriceType.MAX]: ""
    });
  };

  useEffect(() => {
    if (lastSearchType === "sales") {
      dispatch(setForSale());
    } else {
      dispatch(setForRent());
    }

    applySelectedAreas();
  }, []);

  return (
    <S.SearchModuleWrapper>
      <S.ListingsToggle listingType={listingTypeTab}>
        <S.LinkToggle
          aria-checked={listingTypeTab === "rentals"}
          as="button"
          isActive={listingTypeTab === "rentals"}
          onClick={handleSetRent}
          role="radio"
        >
          Rent
        </S.LinkToggle>
        <S.LinkToggle
          aria-checked={listingTypeTab === "sales"}
          as="button"
          isActive={listingTypeTab === "sales"}
          onClick={handleSetBuy}
          role="radio"
        >
          Buy
        </S.LinkToggle>
      </S.ListingsToggle>
      <S.LocationFilterWrapper>
        <HomePageLocationFilterWidget
          areaOptions={areaOptions}
          handleApply={applySelectedAreas}
          rootElementId="#site-wrapper"
        />
      </S.LocationFilterWrapper>
      <S.MaxPriceWrapper isWide={listingTypeTab === "sales"}>
        <S.PriceField id="TextField-minPrice" isWide label="Minimum Price">
          {priceWidgetTest === "has_widget" ? (
            <S.PriceCombobox
              id="min-price-combobox"
              inputMode="numeric"
              onChange={(_, price) =>
                handlePriceChange(price, searchPriceType.MIN)
              }
              options={priceOptions[listingTypeTab]}
              pattern="[0-9]*"
              placeholder="Min"
              value={priceSelections[searchPriceType.MIN]}
            />
          ) : (
            <TextInput
              onChange={e =>
                handlePriceChange(e.target.value, searchPriceType.MIN)
              }
              placeholder="Min"
              value={priceSelections[searchPriceType.MIN]}
            />
          )}
        </S.PriceField>
        <S.PriceField
          id="TextField-withTextarea"
          isWide={listingTypeTab === "sales"}
          label="Maximum Price"
        >
          {priceWidgetTest === "has_widget" ? (
            <S.PriceCombobox
              id="max-price-combobox"
              inputMode="numeric"
              name="max"
              onChange={(_, price) =>
                handlePriceChange(price, searchPriceType.MAX)
              }
              options={priceOptions[listingTypeTab]}
              pattern="[0-9]*"
              placeholder="Max"
              value={priceSelections[searchPriceType.MAX]}
            />
          ) : (
            <TextInput
              onChange={e =>
                handlePriceChange(e.target.value, searchPriceType.MAX)
              }
              placeholder="Max"
              value={priceSelections[searchPriceType.MAX]}
            />
          )}
        </S.PriceField>
        <S.CheckBoxWrapper
          aria-hidden={listingTypeTab === "sales"}
          isWide={listingTypeTab === "sales"}
        >
          <CheckboxInput
            aria-hidden={listingTypeTab === "sales"}
            checked={noFee}
            disabled={listingTypeTab === "sales"}
            label="No fee"
            name="no-fee"
            onChange={() => setNoFee(!noFee)}
            value="No fee"
          />
        </S.CheckBoxWrapper>
      </S.MaxPriceWrapper>
      <S.SearchButton onClick={handleSearchSubmit} theme="PRIMARY">
        <S.SearchIcon
          color="pureWhite"
          desktopPadding="0"
          fontSize="24px"
          iconClass="search"
          mobilePadding={null}
        />
        Search
      </S.SearchButton>
    </S.SearchModuleWrapper>
  );
};

SearchModule.defaultProps = {
  lastSearchType: "rentals",
  priceWidgetTest: "no_widget"
};

SearchModule.propTypes = {
  areaOptions: locations.isRequired,
  lastSearchType: string,
  priceWidgetTest: oneOf(["no_widget", "has_widget"])
};

export default SearchModule;
